import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import type { OwnLoanItem } from './ownLoan.types';
import {isNullOrUndefined} from "../../../../Utilities/commonUtilities";
import {ownLoanApi} from "./ownLoanApi";
import {SYSTEM_TYPES} from "../../../Library/libraryConstants";
import {getSystemTypeTextByValue} from "../../../Library/libraryUtility";
import {PageTitle} from "../../../../Components/Landmarks";
import {OwnLoanSearchFormView} from "./OwnLoanSearchFormView";
import {LayoutFullWidth} from "../../../../Components/Layout";
import {SearchResultsContainer} from "../../../../Components/SearchResults";
import {OwnLoanTable} from "./OwnLoanTable";
import {allow, policyEvents} from "../../../../Components/Authorize";
import {ownLoanItemModel, updateOwnLoanItemModel} from "./ownLoanFactory";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {Button, ButtonLinkStyle} from "../../../../Components/Inputs";
import {getChangedOwnLoanItemsForUpdate} from "./ownLoanUtility";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";

export const OwnLoanManagementContainer = ({
                                               ownLoanSearchCriteria,
                                               handleApiCall,
                                               handleSetOwnLoanSearchCriteria,
                                               isLoading
                                           }: ContextProps) => {

    const [ownLoanItems, setOwnLoanItems] = useState<OwnLoanItem[]>([]);
    const [originalOwnLoanItems, setOriginalOwnLoanItems] = useState<OwnLoanItem[]>([]);

    const [ownLoanSearchDropDowns, setOwnLoanSearchDropDowns] = useState<Record<string, unknown> | null>(null);

    const canManageOwnLoans = allow(policyEvents.MANAGE_OWN_LOANS);

    const loadOwnLoanItemsResult = (result: Record<string, unknown>[]) => {
        const ownLoanItems = result.map((loan: Record<string, unknown>) => ownLoanItemModel(loan));
        setOwnLoanItems(ownLoanItems);
        setOriginalOwnLoanItems(ownLoanItems);
    };

    const handleGetOwnLoanItemsReadyForProcessing = () => {
        handleApiCall(() => ownLoanApi.getOwnItemsReadyForProcessing({
            ...ownLoanSearchCriteria,
        }), loadOwnLoanItemsResult);
    }

    const handleGetOwnLoanSearchDropDowns = () => {
        handleApiCall(() => ownLoanApi.getDropdownListsForOwnLoanSearch(), (results: Record<string, unknown>) => setOwnLoanSearchDropDowns(results));
    }


    const updateOwnLoanItemInState = (ownLoanItemId: string, ownLoanStatus: string | number, notes: string | null) => {

        setOwnLoanItems((currentOwnLoanItems) =>
            currentOwnLoanItems.map((item: OwnLoanItem) =>
                item.ownLoanItemId === ownLoanItemId
                    ? {
                        ...item,
                        ownLoanStatus,
                        ownLoanNotes: notes ? notes : item.ownLoanNotes,
                    }
                    : item
            )
        );
    };

    const syncOriginalOwnLoanItemAfterSave = (updatedOwnLoanItem: OwnLoanItem) => {

        setOriginalOwnLoanItems((currentOwnLoanItems) =>
            currentOwnLoanItems.map((item: OwnLoanItem) =>
                item.ownLoanItemId === updatedOwnLoanItem.ownLoanItemId
                    ? {
                        ...item,
                        ownLoanStatus: updatedOwnLoanItem.ownLoanStatus,
                        ownLoanNotes: updatedOwnLoanItem.ownLoanNotes ? updatedOwnLoanItem.ownLoanNotes : item.ownLoanNotes,
                    }
                    : item
            )
        );
    };

    const handleSaveUpdatedOwnLoanItem = (updatedOwnLoanItem: OwnLoanItem) => {
        updateOwnLoanItemInState(updatedOwnLoanItem.ownLoanItemId, updatedOwnLoanItem.ownLoanStatus, updatedOwnLoanItem.ownLoanNotes);

        const ownLoanItemForSaving = [
            updateOwnLoanItemModel({
                ownLoanItemId: updatedOwnLoanItem.ownLoanItemId,
                ownLoanStatus: updatedOwnLoanItem.ownLoanStatus,
                ownLoanNotes: updatedOwnLoanItem.ownLoanNotes,
            })
        ];

        handleApiCall(() => ownLoanApi.updateOwnLoans(ownLoanItemForSaving), syncOriginalOwnLoanItemAfterSave);
    }

    const handleSaveUpdatedOwnLoanItems = () => {

        const updatedOwnLoanItems = getChangedOwnLoanItemsForUpdate(ownLoanItems, originalOwnLoanItems);

        if (updatedOwnLoanItems.length === 0) {
            alert("No changes to save.");
            return;
        }

        if (confirm(`Are you sure you want to save ${updatedOwnLoanItems.length} updated OWN loan item(s)?`))
            handleApiCall(() => ownLoanApi.updateOwnLoans(updatedOwnLoanItems), handleGetOwnLoanItemsReadyForProcessing);
    };

    const handleExportLoansClick = () => {
        if (isArrayNullOrEmpty(ownLoanItems)) {
            alert("Search criteria has no results. Adjust your search and try again.")
        } else {
            handleApiCall(() => ownLoanApi.getOwnLoansReport(ownLoanSearchCriteria));
        }
    };


    useEffect(() => {
        handleGetOwnLoanItemsReadyForProcessing();
    }, [ownLoanSearchCriteria]);


    useEffect(() => {
        handleGetOwnLoanSearchDropDowns();
    }, []);

    if (isNullOrUndefined(ownLoanSearchDropDowns)) return;

    return <>
        <PageTitle h1={`${getSystemTypeTextByValue(SYSTEM_TYPES.Own.toString())} Library Management`}/>

        <OwnLoanSearchFormView
            ownLoanSearchCriteria={ownLoanSearchCriteria}
            ownLoanSearchDropDowns={ownLoanSearchDropDowns}
            handleSetOwnLoanSearchCriteria={handleSetOwnLoanSearchCriteria}
            isLoading={isLoading}
        />

        <LayoutFullWidth>
            <SearchResultsContainer>
            <div>
                {
                    ownLoanItems &&
                    <>
                        <GridContainer isReverseOrder className={`form__header`} hasMarginX>
                            <GridCell
                                medium_columns={gridConstants.column.THREE}
                                small_columns={gridConstants.column.SIX}>
                                <Button
                                    disabled={isLoading}
                                    isPrimary
                                    label="Save Updates"
                                    name="btnSave"
                                    onClick={handleSaveUpdatedOwnLoanItems}
                                />
                            </GridCell>
                            <GridCell
                                medium_columns={gridConstants.column.ONE}>
                                <ButtonLinkStyle
                                    isCollapsed
                                    label={`Export Own Items`}
                                    name="btnExportLoans"
                                    onClick={handleExportLoansClick}
                                />
                            </GridCell>
                        </GridContainer>
                        <OwnLoanTable
                            ownLoanItems={ownLoanItems}
                            ownLoanSearchCriteria={ownLoanSearchCriteria}
                            canManageOwnLoans={canManageOwnLoans}
                            handleSetOwnLoanSearchCriteria={handleSetOwnLoanSearchCriteria}
                            handleSaveUpdatedOwnLoanItem={handleSaveUpdatedOwnLoanItem}
                            updateOwnLoanItemInState={updateOwnLoanItemInState}
                        />
                    </>
                }
            </div>
            </SearchResultsContainer>
        </LayoutFullWidth>
    </>;
};
